import ClientLogger from "../../../../logging/ClientLogger";
import CreateWorkScopeSpecificationError from "../error/CreateWorkScopeSpecificationError";
import DeleteWorkScopeSpecificationError from "../error/DeleteWorkScopeSpecificationError";
import Deserializer from "../../../../util/data/serialization/Deserializer";
import Envelope from "../../../../envelope/Envelope";
import GetWorkScopeSpecificationByIdError from "../error/GetWorkScopeSpecificationByIdError";
import ResourceNotFoundError from "../../../../ResourceNotFoundError";
import Serializer from "../../../../util/data/serialization/Serializer";
import UpdateWorkScopeSpecificationError from "../error/UpdateWorkScopeSpecificationError";
import WorkScopeSpecificationDAO from "../WorkScopeSpecificationDAO";
import { WorkSpecification } from "../../../../../models";
import _ from "lodash";

export default class MockDataStoreWorkScopeSpecificationDAO implements WorkScopeSpecificationDAO {
    private static readonly STORAGE_NAME = "mock-work-scope-specifications";
    private static readonly GET_BY_ID_ATTEMPT_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.GetById.Attempt";
    private static readonly GET_BY_ID_FAILURE_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.GetById.Failure";
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Create.Failure";
    private static readonly UPDATE_ATTEMPT_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Update.Attempt";
    private static readonly UPDATE_FAILURE_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Update.Failure";
    private static readonly DELETE_ATTEMPT_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Delete.Attempt";
    private static readonly DELETE_FAILURE_METRIC_NAME = "MockDataStoreWorkScopeSpecificationDAO.Delete.Failure";

    private readonly serializer: Serializer<Envelope, string>;
    private readonly deserializer: Deserializer<string, Envelope>;
    private readonly logger: ClientLogger;

    constructor(
        serializer: Serializer<Envelope, string>,
        deserializer: Deserializer<string, Envelope>,
        dataStore: any, // Not used in mock but needed for interface consistency
        logger: ClientLogger
    ) {
        this.serializer = serializer;
        this.deserializer = deserializer;
        this.logger = logger;
        // dataStore parameter is ignored in mock implementation
    }

    private loadMock(): WorkSpecification[] {
        let mock = new Array<WorkSpecification>();
        const mockStr = localStorage.getItem(MockDataStoreWorkScopeSpecificationDAO.STORAGE_NAME);

        if (mockStr) {
            mock = JSON.parse(mockStr) as WorkSpecification[];
        }

        return mock;
    }

    private saveMock(mock: WorkSpecification[]): void {
        localStorage.setItem(MockDataStoreWorkScopeSpecificationDAO.STORAGE_NAME, JSON.stringify(mock));
    }

    public async listByIssueId(issueId: string): Promise<WorkSpecification[]> {
        const mock = this.loadMock();
        return mock.filter((wss): boolean => {
            return wss.issueId === issueId;
        });
    }

    public async listByDimensionId(dimensionId: string): Promise<WorkSpecification[]> {
        const mock = this.loadMock();
        return mock.filter((wss): boolean => {
            return wss.dimensionId === dimensionId;
        });
    }

    public async getById(id: string): Promise<WorkSpecification> {
        try {
            this.logger.info(
                `Getting WorkSpecification with id=${id}`,
                id,
                [MockDataStoreWorkScopeSpecificationDAO.GET_BY_ID_ATTEMPT_METRIC_NAME]
            );
            const mock = this.loadMock();
            const result = mock.find((wss): boolean => {
                return wss.id === id;
            });
            
            if (!result) {
                throw new ResourceNotFoundError("WorkSpecification");
            }
            return result;
        } catch (error) {
            const errorMessage = `Unable to get WorkSpecification with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [MockDataStoreWorkScopeSpecificationDAO.GET_BY_ID_FAILURE_METRIC_NAME]
            );
            throw new GetWorkScopeSpecificationByIdError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async create(workSpecification: WorkSpecification): Promise<WorkSpecification> {
        try {
            this.logger.info(
                `Creating WorkSpecification with id=${workSpecification.id}`,
                workSpecification,
                [MockDataStoreWorkScopeSpecificationDAO.CREATE_ATTEMPT_METRIC_NAME]
            );
            const workSpecificationToCreate = new WorkSpecification({
                ...workSpecification,
                localLastUpdatedTime: Date.now()
            });
            
            const mock = this.loadMock();
            const exists = mock.find((value): boolean => {
                return value.id === workSpecificationToCreate.id;
            });

            if (exists) {
                throw Error("WorkSpecification already exists");
            }

            mock.push(workSpecificationToCreate);
            this.saveMock(mock);

            return workSpecificationToCreate;
        } catch (error) {
            const errorMessage = `Unable to create WorkSpecification with id=${workSpecification.id}`;
            this.logger.error(
                errorMessage,
                error,
                [MockDataStoreWorkScopeSpecificationDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new CreateWorkScopeSpecificationError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async update(workSpecification: WorkSpecification): Promise<WorkSpecification> {
        try {
            const existingWorkSpecification: WorkSpecification = await this.getById(workSpecification.id);
            if (_.isEqual(existingWorkSpecification, workSpecification)) {
                this.logger.info(
                    `No changes detected in workSpecification ${workSpecification.id}, skipping update`,
                    workSpecification,
                    [MockDataStoreWorkScopeSpecificationDAO.UPDATE_ATTEMPT_METRIC_NAME]
                );
                return workSpecification;
            }
            this.logger.info(
                `Updating WorkSpecification with id=${workSpecification.id}`,
                workSpecification,
                [MockDataStoreWorkScopeSpecificationDAO.UPDATE_ATTEMPT_METRIC_NAME]
            );
            
            const result: WorkSpecification = {
                ...existingWorkSpecification,
                measurement: workSpecification.measurement,
                workTypeId: workSpecification.workTypeId,
                description: workSpecification.description,
                itemNumber: workSpecification.itemNumber,
                localLastUpdatedTime: Date.now(),
                issueId: workSpecification.issueId,
                dimensionId: workSpecification.dimensionId
            };

            const mock = this.loadMock();
            const index = mock.findIndex((value): boolean => {
                return value.id === result.id;
            });

            if (index >= 0) {
                mock[index] = result;
                this.saveMock(mock);
            }

            return result;
        } catch (error) {
            const errorMessage = `Unable to update WorkSpecification with id=${workSpecification.id}`;
            this.logger.error(
                errorMessage,
                error,
                [MockDataStoreWorkScopeSpecificationDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new UpdateWorkScopeSpecificationError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async delete(workSpecificationId: string): Promise<void> {
        try {
            this.logger.info(
                `Deleting WorkSpecification with id=${workSpecificationId}`,
                workSpecificationId,
                [MockDataStoreWorkScopeSpecificationDAO.DELETE_ATTEMPT_METRIC_NAME]
            );
            const mock = this.loadMock();
            const filteredMock = mock.filter((wss): boolean => {
                return wss.id !== workSpecificationId;
            });
            
            this.saveMock(filteredMock);
        } catch (error) {
            const errorMessage = `Unable to delete WorkSpecification with id=${workSpecificationId}`;
            this.logger.error(
                errorMessage,
                error,
                [MockDataStoreWorkScopeSpecificationDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw new DeleteWorkScopeSpecificationError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async queryWorkScopeSpecificationsByIssueId(issueId: string): Promise<Envelope[]> {
        const workScopeSpecificationRecords: WorkSpecification[] = await this.listByIssueId(issueId);
        const result: Envelope[] = [];
        workScopeSpecificationRecords.forEach((wssRecord, index) => {
            const wssEnvelope: Envelope = {
                header: {
                    dataType: "WSS",
                    id: wssRecord.id,
                    variant: "Default",
                    version: wssRecord.version!
                },
                body: this.deserializer.deserialize(wssRecord.body!),
            };
            result.push(wssEnvelope);
        });
        return result;
    }

    public async queryWorkScopeSpecificationsByDimensionId(dimensionId: string): Promise<Envelope[]> {
        const workScopeSpecificationRecords: WorkSpecification[] = await this.listByDimensionId(dimensionId);
        const result: Envelope[] = [];
        workScopeSpecificationRecords.forEach((wssRecord, index) => {
            const wssEnvelope: Envelope = {
                header: {
                    dataType: "WSS",
                    id: wssRecord.id,
                    variant: "Default",
                    version: wssRecord.version!
                },
                body: this.deserializer.deserialize(wssRecord.body!),
            };
            result.push(wssEnvelope);
        });
        return result;
    }

    public async createWorkScopeSpecification(workScopeSpecification: Envelope, dimensionId: string, issueId: string): Promise<WorkSpecification> {
        const serializedWSS: string = this.serializer.serialize(workScopeSpecification.body);
        const wssRecord: WorkSpecification = new WorkSpecification({
            dimensionId: dimensionId,
            issueId,
            version: workScopeSpecification.header.version,
            body: serializedWSS
        });
        return await this.create(wssRecord);
    }

    public async editWorkScopeSpecification(workScopeSpecification: Envelope): Promise<WorkSpecification> {
        const serializedWSSBody: string = this.serializer.serialize(workScopeSpecification.body);
        const originalWSS: WorkSpecification = await this.getById(workScopeSpecification.header.id);
        if (_.isEqual(serializedWSSBody, originalWSS.body)) {
            return originalWSS;
        }
        
        const updatedWSS: WorkSpecification = {
            ...originalWSS,
            body: serializedWSSBody,
            localLastUpdatedTime: Date.now()
        };
        
        const mock = this.loadMock();
        const index = mock.findIndex((value): boolean => {
            return value.id === updatedWSS.id;
        });

        if (index >= 0) {
            mock[index] = updatedWSS;
            this.saveMock(mock);
        }
        
        return updatedWSS;
    }

    public async deleteWorkScopeSpecification(workScopeSpecification: Envelope): Promise<void> {
        await this.delete(workScopeSpecification.header.id);
    }
} 